// © 2026 KaraokeClubs. All rights reserved.
// Crowd verification widget shown on the venue detail page.
// One tap lets a visitor confirm or deny that karaoke was actually on tonight.
// The live tally is the most reliable real-time signal we have.

"use client";

import { useEffect, useState } from "react";
import { t } from "@/lib/i18n";
import type { Locale } from "@/lib/types";

interface Props {
  venueId: string;
  locale: Locale;
}

type Verdict = "confirmed" | "denied";

export default function CrowdReport({ venueId, locale }: Props) {
  const [confirmed, setConfirmed] = useState(0);
  const [denied, setDenied] = useState(0);
  const [picked, setPicked] = useState<Verdict | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/api/report?venue_id=${encodeURIComponent(venueId)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setConfirmed(d.confirmed ?? 0);
        setDenied(d.denied ?? 0);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [venueId]);

  async function report(verdict: Verdict) {
    if (busy) return;
    setBusy(true);
    setPicked(verdict);
    try {
      const res = await fetch("/api/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ venue_id: venueId, verdict }),
      });
      const d = await res.json();
      if (res.ok) {
        setConfirmed(d.confirmed ?? confirmed);
        setDenied(d.denied ?? denied);
      }
    } catch {
      /* swallow — the tap is best-effort */
    } finally {
      setBusy(false);
    }
  }

  const total = confirmed + denied;

  return (
    <div className="mt-2 mb-1 rounded-2xl bg-zinc-900/60 border border-white/[0.06] p-4">
      <p className="text-[13px] font-semibold text-zinc-200 mb-0.5">
        {t(locale, "report_q")}
      </p>
      <p className="text-[12px] text-zinc-500 mb-3">
        {total > 0
          ? t(locale, "report_tally")
              .replace("{yes}", String(confirmed))
              .replace("{no}", String(denied))
          : t(locale, "report_hint")}
      </p>

      {picked ? (
        <p className="text-[13px] text-violet-300 font-medium flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {t(locale, "report_thanks")}
        </p>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => report("confirmed")}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                       bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 font-semibold text-[13px]
                       active:scale-[0.98] transition-all touch-manipulation disabled:opacity-60 hover:bg-emerald-500/15"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {t(locale, "report_yes")}
          </button>
          <button
            onClick={() => report("denied")}
            disabled={busy}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                       bg-zinc-800 border border-white/[0.08] text-zinc-400 font-semibold text-[13px]
                       active:scale-[0.98] transition-all touch-manipulation disabled:opacity-60 hover:text-zinc-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            {t(locale, "report_no")}
          </button>
        </div>
      )}
    </div>
  );
}
